import { useCallback } from "react";

import { Button, ButtonsWrapper, Info } from "components/elements";
import { WarningModal } from "components/modals";
import { ActionMenu, MenuItem } from "components/popups";
import { RestrictedArea, ROLES } from "containers/Permissions";
import { useDeleteVariant, useDeleteVariantDraft } from "hooks/tivoApps";
import { TiVoAppProvider, TiVoAppVariant } from "hooks/tivoApps/types";
import { useToggle } from "hooks/utils";

import { MAIN_ROUTE, VARIANT_DRAFT_ID } from "../constants";
import { MENU, TEXTS } from "./constants";
import styles from "./TiVoAppRowMenu.scss";

interface TiVoAppRowMenuProps {
  id: TiVoAppProvider["id"];
  variantId: TiVoAppVariant["id"];
}

export function TiVoAppRowVariantMenu({ id, variantId }: TiVoAppRowMenuProps) {
  const deleteVariant = useDeleteVariant(id.toString(), variantId.toString());
  const deleteDraft = useDeleteVariantDraft(id);
  const [archiveModalOpen, toggleArchiveModalOpen] = useToggle(false);
  const warningModalDataTestId = "delete-variant-modal";

  const isDraft = variantId === VARIANT_DRAFT_ID;
  const {
    mutate: archive,
    isPending: archivePending,
    error: archiveError,
    reset: archiveReset,
  } = isDraft ? deleteDraft : deleteVariant;

  const handleArchive = useCallback(() => {
    archive(undefined, {
      onSuccess: toggleArchiveModalOpen,
    });
  }, [archive, toggleArchiveModalOpen]);

  return (
    <>
      <WarningModal
        actions={
          <ButtonsWrapper position="modal">
            <Button
              type="normal"
              dataTestId={`${warningModalDataTestId}-cancel-button`}
              onClick={toggleArchiveModalOpen}
            >
              {TEXTS.CANCEL_BUTTON_LABEL}
            </Button>
            <Button
              disabled={archivePending}
              processing={archivePending}
              type="red"
              dataTestId={`${warningModalDataTestId}-confirm-button`}
              onClick={handleArchive}
            >
              {TEXTS.CONFIRM_BUTTON_LABEL}
            </Button>
          </ButtonsWrapper>
        }
        isOpen={archiveModalOpen}
        size="medium"
        title={TEXTS.ARCHIVE_MODAL_HEADER}
        dataTestId={warningModalDataTestId}
        onClose={toggleArchiveModalOpen}
      >
        <p className={styles.archiveWarning}>{TEXTS.ARCHIVE_VARIANT_WARNING}</p>
        {archiveError && <Info type="error">{archiveError.message}</Info>}
      </WarningModal>
      <ActionMenu alignment="left-start" dataTestId="variant-menu">
        <MenuItem
          to={`/${MAIN_ROUTE}/details/${id}/variant/${variantId}`}
          dataTestId="variant-details-menu-item"
        >
          {MENU.VARIANT.DETAILS}
        </MenuItem>
        <RestrictedArea allowed={[ROLES.tivo.provider]} showFallback={false}>
          <MenuItem
            to={
              isDraft
                ? `/${MAIN_ROUTE}/create/${id}/variant/`
                : `/${MAIN_ROUTE}/edit/${id}/variant/${variantId}`
            }
            dataTestId="variant-edit-menu-item"
          >
            {MENU.VARIANT.EDIT}
          </MenuItem>
        </RestrictedArea>
        <RestrictedArea allowed={[ROLES.tivo.admin]} showFallback={false}>
          <MenuItem
            dataTestId="variant-delete-menu-item"
            onClick={() => {
              archiveReset();
              toggleArchiveModalOpen();
            }}
          >
            {MENU.VARIANT.DELETE}
          </MenuItem>
        </RestrictedArea>
      </ActionMenu>
    </>
  );
}
